import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Mail, User, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WaitlistForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/waitlist`, {
        name: formData.name,
        email: formData.email,
        message: formData.message
      });

      if (response.data.success) {
        toast.success('Thank you! We\'ll be in touch soon.');
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Waitlist submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-32 px-6 lg:px-8 bg-slate-950 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <motion.div
        className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />

      <div className="relative z-10 max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Join the Waitlist
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Be among the first to experience the future of family healthcare management.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="space-y-6 bg-slate-900/50 backdrop-blur-xl p-8 md:p-12 rounded-3xl border border-slate-800 shadow-2xl"
        >
          <div className="relative">
            <User className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Your Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 h-14 rounded-xl focus:border-teal-500 focus:ring-teal-500 transition-all duration-300"
            />
          </div>

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              type="email"
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 h-14 rounded-xl focus:border-teal-500 focus:ring-teal-500 transition-all duration-300"
            />
          </div>

          <div className="relative">
            <MessageSquare className="absolute left-4 top-6 h-5 w-5 text-slate-400" />
            <Textarea
              placeholder="Tell us about your family healthcare needs (optional)"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="pl-12 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 rounded-xl focus:border-teal-500 focus:ring-teal-500 min-h-32 transition-all duration-300"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white h-14 text-lg font-semibold rounded-xl shadow-lg hover:shadow-teal-500/50 transition-all duration-300 hover:scale-[1.02]"
          >
            {loading ? 'Submitting...' : 'Join Waitlist'}
          </Button>
        </motion.form>
      </div>
    </section>
  );
};

export default WaitlistForm;